import { useCallback, useEffect, useMemo, useState } from "react";
import { getMyRegistrations } from "../api/getMyRegistrations/getMyRegistrationsApi";
import { MyRegistrationItem } from "../api/getMyRegistrations/types";

export type RegistrationGroups = {
  thisWeek: MyRegistrationItem[];
  laterThisMonth: MyRegistrationItem[];
  future: MyRegistrationItem[];
  past: MyRegistrationItem[];
};

function groupByTimeHorizon(items: MyRegistrationItem[]): RegistrationGroups {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const oneWeekOut = new Date(startOfToday);
  oneWeekOut.setDate(startOfToday.getDate() + 7);
  const endOfThisMonth = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    23,
    59,
    59,
  );

  const thisWeek: MyRegistrationItem[] = [];
  const laterThisMonth: MyRegistrationItem[] = [];
  const future: MyRegistrationItem[] = [];
  const past: MyRegistrationItem[] = [];

  for (const r of items) {
    if (!r.startDate) {
      // Date TBD: treat as future so it doesn't disappear into past.
      future.push(r);
      continue;
    }
    const d = new Date(r.startDate);
    if (d < startOfToday) {
      past.push(r);
    } else if (d < oneWeekOut) {
      thisWeek.push(r);
    } else if (d <= endOfThisMonth) {
      laterThisMonth.push(r);
    } else {
      future.push(r);
    }
  }

  // Upcoming buckets ascend (soonest first). Past descends (most recent first).
  const byStartAsc = (a: MyRegistrationItem, b: MyRegistrationItem) =>
    (a.startDate ?? "").localeCompare(b.startDate ?? "");
  const byStartDesc = (a: MyRegistrationItem, b: MyRegistrationItem) =>
    (b.startDate ?? "").localeCompare(a.startDate ?? "");

  thisWeek.sort(byStartAsc);
  laterThisMonth.sort(byStartAsc);
  future.sort(byStartAsc);
  past.sort(byStartDesc);

  return { thisWeek, laterThisMonth, future, past };
}

export function useMyRegistrations() {
  const [registrations, setRegistrations] = useState<MyRegistrationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getMyRegistrations();
      setRegistrations(response.data.registrations);
    } catch {
      setError("Failed to load registrations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const groups = useMemo(
    () => groupByTimeHorizon(registrations),
    [registrations],
  );

  const upcomingCount =
    groups.thisWeek.length +
    groups.laterThisMonth.length +
    groups.future.length;

  return {
    groups,
    upcomingCount,
    pastCount: groups.past.length,
    loading,
    error,
    refetch: fetch,
  };
}
